import { create } from 'zustand';
import { useAuthStore } from '../../auth/stores/authStore';
import { previewSiteReimport, executeSiteReimport } from '../api/sitesApi';
import { createLabourItemDefinition } from '../../labour/api/labourItemsApi';
import {
  BaseState,
  createAuthHelper,
  createLoadingWrapper,
} from '../../../shared/stores/storeHelpers';
import type {
  ReimportPreviewResponse,
  ReimportExecuteResponse,
} from '../types/reimportTypes';

interface CreateItemOptions {
  refreshData?: boolean;
  siteId?: number;
  selectedFile?: File;
  isBatchMode?: boolean;
}

interface SiteReimportState extends BaseState {
  // File
  selectedFile?: File;

  // Table
  sortBy: string | null;
  sortDirection: 'asc' | 'desc';
  filterText: string;
  expandedRows: Set<string>;

  // Preview data
  previewData: ReimportPreviewResponse | null;

  // Labour item creation
  creatingLabourItems: boolean;
  creationErrors: string[];
  createdItems: Set<string>;

  // Execute
  executing: boolean;
  executeResult: ReimportExecuteResponse | null;

  // Success snackbar (persists after panel close)
  successMessage: string | null;
}

interface SiteReimportActions {
  // File
  setSelectedFile: (file?: File) => void;
  validateCsvFile: (file: File) => string | null;

  // Table
  setSorting: (column: string | null, direction: 'asc' | 'desc') => void;
  setFilterText: (text: string) => void;
  toggleRowExpansion: (plotNumber: string) => void;

  // API
  uploadAndPreview: (siteId: number, file: File) => Promise<void>;
  executeReimport: (
    siteId: number,
    file: File
  ) => Promise<ReimportExecuteResponse | null>;
  clearError: () => void;

  // Labour item creation
  createMissingLabourItem: (
    itemName: string,
    options?: CreateItemOptions
  ) => Promise<void>;
  createAllMissingLabourItems: (
    siteId: number,
    selectedFile?: File
  ) => Promise<void>;
  clearLabourItemCreationState: () => void;

  // Success snackbar
  clearSuccessMessage: () => void;

  // Reset
  resetAll: () => void;
}

const getInitialState = (): SiteReimportState => ({
  selectedFile: undefined,
  sortBy: null,
  sortDirection: 'asc',
  filterText: '',
  expandedRows: new Set(),
  previewData: null,
  loading: false,
  error: null,
  creatingLabourItems: false,
  creationErrors: [],
  createdItems: new Set(),
  executing: false,
  executeResult: null,
  successMessage: null,
});

export const useSiteReimportStore = create<
  SiteReimportState & SiteReimportActions
>((set, get) => {
  const { requireAuth } = createAuthHelper<
    SiteReimportState & SiteReimportActions
  >(() => useAuthStore.getState().token);
  const { withLoading, withLoadingReturn } =
    createLoadingWrapper<SiteReimportState>();

  const handleLabourItemCreationSuccess = async (
    itemName: string,
    refreshData: boolean,
    isBatchMode: boolean,
    siteId?: number,
    selectedFile?: File
  ): Promise<void> => {
    const { createdItems } = get();
    const newCreatedItems = new Set(createdItems);
    newCreatedItems.add(itemName);

    set({
      createdItems: newCreatedItems,
      ...(isBatchMode ? {} : { creatingLabourItems: false }),
    });

    if (refreshData && siteId && selectedFile) {
      const { uploadAndPreview } = get();
      await uploadAndPreview(siteId, selectedFile);
    }
  };

  const handleLabourItemCreationError = (
    itemName: string,
    errorMessage: string,
    isBatchMode: boolean
  ): void => {
    const { creationErrors } = get();
    const newErrors = [
      ...creationErrors,
      `Failed to create "${itemName}": ${errorMessage}`,
    ];
    set({
      creationErrors: newErrors,
      ...(isBatchMode ? {} : { creatingLabourItems: false }),
    });
  };

  return {
    ...getInitialState(),

    // File
    setSelectedFile: (file?: File) => set({ selectedFile: file }),

    validateCsvFile: (file: File): string | null => {
      if (!file.name.endsWith('.csv')) {
        return 'Please select a CSV file';
      }
      return null;
    },

    // Table
    setSorting: (column: string | null, direction: 'asc' | 'desc') => {
      set({ sortBy: column, sortDirection: direction });
    },

    setFilterText: (text: string) => set({ filterText: text }),

    toggleRowExpansion: (plotNumber: string) => {
      const { expandedRows } = get();
      const newExpandedRows = new Set(expandedRows);
      if (newExpandedRows.has(plotNumber)) {
        newExpandedRows.delete(plotNumber);
      } else {
        newExpandedRows.add(plotNumber);
      }
      set({ expandedRows: newExpandedRows });
    },

    // API
    uploadAndPreview: async (siteId: number, file: File) => {
      const token = requireAuth(set);
      if (!token) return;

      await withLoading(
        set,
        async () => {
          const result = await previewSiteReimport(siteId, file);

          set({
            previewData: result,
            error: null,
          });
        },
        {
          customErrorHandler: async error => {
            console.error('Error during re-import preview:', error);
            const { getErrorMessage } = await import('@billda/shared-ui');
            set({ error: getErrorMessage(error) });
          },
        }
      );
    },

    executeReimport: async (
      siteId: number,
      file: File
    ): Promise<ReimportExecuteResponse | null> => {
      const token = requireAuth(set);
      if (!token) return null;

      const result = await withLoadingReturn(
        set,
        async () => {
          set({ executing: true, executeResult: null });

          const response = await executeSiteReimport(siteId, file);

          set({
            executeResult: response,
            executing: false,
            error: null,
          });
          return response;
        },
        {
          skipLoading: true,
          customErrorHandler: async error => {
            console.error('Error during re-import:', error);
            const { getErrorMessage } = await import('@billda/shared-ui');
            set({
              error: getErrorMessage(error),
              executing: false,
            });
          },
        }
      );

      return result;
    },

    clearError: () => set({ error: null }),

    // Labour item creation
    createMissingLabourItem: async (
      itemName: string,
      options: CreateItemOptions = {}
    ) => {
      const {
        refreshData = true,
        siteId,
        selectedFile,
        isBatchMode = false,
      } = options;
      const token = requireAuth(set);
      const { createdItems } = get();

      if (!token) return;

      if (createdItems.has(itemName)) {
        return;
      }

      if (!isBatchMode) {
        set({ creatingLabourItems: true });
      }

      try {
        await createLabourItemDefinition({ name: itemName });
        await handleLabourItemCreationSuccess(
          itemName,
          refreshData,
          isBatchMode,
          siteId,
          selectedFile
        );
      } catch (error) {
        const { getErrorMessage } = await import('@billda/shared-ui');
        handleLabourItemCreationError(
          itemName,
          getErrorMessage(error),
          isBatchMode
        );
      }
    },

    createAllMissingLabourItems: async (
      siteId: number,
      selectedFile?: File
    ) => {
      const { previewData, createMissingLabourItem } = get();

      if (!previewData || !previewData.missingLabourDefinitions.length) {
        return;
      }

      set({ creatingLabourItems: true, creationErrors: [] });

      for (const itemName of previewData.missingLabourDefinitions) {
        const { createdItems } = get();
        if (!createdItems.has(itemName)) {
          await createMissingLabourItem(itemName, {
            refreshData: false,
            siteId,
            selectedFile,
            isBatchMode: true,
          });
        }
      }

      const { createdItems, creationErrors } = get();
      const allCreated = previewData.missingLabourDefinitions.every(item =>
        createdItems.has(item)
      );

      if (allCreated && creationErrors.length === 0 && selectedFile) {
        const { uploadAndPreview } = get();
        await uploadAndPreview(siteId, selectedFile);
      }

      set({ creatingLabourItems: false });
    },

    clearLabourItemCreationState: () =>
      set({
        createdItems: new Set<string>(),
        creationErrors: [],
        creatingLabourItems: false,
      }),

    // Success snackbar
    clearSuccessMessage: () => set({ successMessage: null }),

    // Reset
    resetAll: () => {
      const { successMessage } = get();
      set({ ...getInitialState(), successMessage });
    },
  };
});
